import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Calculator } from "@phosphor-icons/react";
import MasterExcelToolbar from "../components/MasterExcelToolbar";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const rupee = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

// Colour the profit % pill green / yellow / red per user-specified bands
// (see CostSheetEditor.js for the canonical definition).
const profitTone = (pct) => {
  if (pct < 0)  return { bg: "#FEE2E2", fg: "#7F1D1D", label: "High Risk" };
  if (pct < 5)  return { bg: "#FEE2E2", fg: "#991B1B", label: "Risk" };
  if (pct < 10) return { bg: "#FFEDD5", fg: "#9A3412", label: "Okay" };
  if (pct < 15) return { bg: "#FEF9C3", fg: "#854D0E", label: "Fair" };
  if (pct < 20) return { bg: "#DCFCE7", fg: "#166534", label: "Good" };
  return          { bg: "#D1FAE5", fg: "#064E3B", label: "Healthy" };
};

export default function CostSheet() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cost-sheets`);
      setRows(r.data || []);
    } catch (e) {
      toast.error("Failed to load cost sheets");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this cost sheet? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/cost-sheets/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter(r => (r.part_no || "").toLowerCase().includes(q) ||
                       (r.part_name || "").toLowerCase().includes(q) ||
                       (r.customer || "").toLowerCase().includes(q))
    : rows;
  // Sort ascending by profit_pct so the riskiest sheets surface at the
  // top of the table (High Risk → Risk → Okay → Fair → Good → Healthy).
  // Null/undefined profit_pct rows sink to the bottom.
  const sorted = filtered.slice().sort((a, b) => {
    const av = a.profit_pct;
    const bv = b.profit_pct;
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    return av - bv;
  });

  return (
    <Layout>
      <div className="space-y-4 max-w-7xl mx-auto p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black text-[#1D2951] flex items-center gap-2">
              <Calculator size={24} weight="bold" className="text-[#F5B301]" />
              Cost Sheet
            </h1>
            <p className="text-xs text-[#64748B] mt-1">
              Per-part manufacturing cost estimation with dynamic operation-based sections and profit analysis.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search part / customer" className="h-9 pl-7 w-64 text-sm"
                data-testid="cost-sheet-search" />
            </div>
            <MasterExcelToolbar
              basePath="/cost-sheets"
              entityLabel="Cost Sheets"
              onImportDone={load}
              size="sm"
            />
            <Button className="bg-[#1D2951] hover:bg-[#2A3B73] text-white h-9"
              onClick={() => navigate("/cost-sheet/new")} data-testid="cost-sheet-new-btn">
              <Plus size={14} className="mr-1" /> New Cost Sheet
            </Button>
          </div>
        </div>

        <Card className="border border-[#E2E8F0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Part Number</th>
                  <th className="px-3 py-3 text-left font-semibold">Part Name</th>
                  <th className="px-3 py-3 text-left font-semibold">Customer</th>
                  <th className="px-3 py-3 text-right font-semibold">Selling Price</th>
                  <th className="px-3 py-3 text-right font-semibold">Manufacturing Cost</th>
                  <th className="px-3 py-3 text-right font-semibold">Profit</th>
                  <th className="px-3 py-3 text-center font-semibold">Profit %</th>
                  <th className="px-3 py-3 text-center font-semibold w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {loading && <tr><td colSpan={8} className="py-12 text-center text-[#64748B]">Loading…</td></tr>}
                {!loading && sorted.length === 0 && (
                  <tr><td colSpan={8} className="py-16 text-center text-[#94A3B8]">
                    No cost sheets yet — click <b>New Cost Sheet</b> to create your first estimate.
                  </td></tr>
                )}
                {sorted.map((r) => {
                  const tone = profitTone(r.profit_pct || 0);
                  return (
                    <tr key={r.id} className="hover:bg-[#F8FAFC]" data-testid={`cost-sheet-row-${r.part_no}`}>
                      <td className="px-4 py-3 font-mono font-bold text-[#0F172A]">{r.part_no || "—"}</td>
                      <td className="px-3 py-3 text-[#334155]">{r.part_name || "—"}</td>
                      <td className="px-3 py-3 text-[#475569]">{r.customer || "—"}</td>
                      <td className="px-3 py-3 text-right font-mono text-[#334155]">{rupee(r.selling_price)}</td>
                      <td className="px-3 py-3 text-right font-mono text-[#334155]">{rupee(r.manufacturing_cost)}</td>
                      <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: tone.fg }}>{rupee(r.profit)}</td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold"
                          style={{ background: tone.bg, color: tone.fg }}>
                          {Number(r.profit_pct || 0).toFixed(1)}% · {tone.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center whitespace-nowrap">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                          onClick={() => navigate(`/cost-sheet/${r.id}`)}
                          data-testid={`cost-sheet-edit-${r.part_no}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-[#DC2626]"
                          onClick={() => remove(r.id)}
                          data-testid={`cost-sheet-delete-${r.part_no}`}>
                          <Trash size={14} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
